import { D } from '@mobily/ts-belt';
import MigrationIcon from '@mui/icons-material/Archive';
import HistoryIcon from '@mui/icons-material/History';
import DraftIcon from '@mui/icons-material/InsertDriveFile';
import InProgressIcon from '@mui/icons-material/Today';
import { Box, styled } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  NumberField,
  RecordContextProvider,
  SelectArrayInput,
  TextField,
  TopToolbar,
  useListContext,
  useRecordContext,
} from 'react-admin';

import {
  B2bCampaignStatus,
  B2bCampaignStatusTabMapping,
  B2bCampaignTab,
} from '@Common/types/bo/b2b/campaigns';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { CollectiveOperationListItem } from '@Common/types/bo_external/collective_operations';

import {
  DatagridTyped,
  FilterButton,
  List,
  VerticalTab,
  VerticalTabsLayout,
} from '@sh4res/bo-common/components';
import { B2bCampaignsCreate } from '@sh4res/bo-common/components/B2bCampaigns/B2bCampaignsCreate';
import { MigratedCampaignsListView } from '@sh4res/bo-common/components/B2bCampaigns/B2bMigratedCampaignsList';
import { CampaignStatusField } from '@sh4res/bo-common/components/B2bCampaigns/CampaignStatusField';
import { b2bCampaignStatusChoices } from '@sh4res/bo-common/components/B2bCampaigns/choices';
import {
  CurrencyField,
  IdField,
  ProductsField,
  SelectChipField,
  TextArrayInput,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { createFilters } from '@sh4res/bo-common/utils/create_filters';

import { collectiveOperationTypeChoices } from '@Resources/collective_operations/choices';

type GetCollectiveOperationsQuery = BoListQueryBase;

const testId = 'BoExternalCollectiveOperationsList';

const collectiveOperationsFilters = (statusFilters: B2bCampaignStatus[]) =>
  createFilters<GetCollectiveOperationsQuery>({
    numeric_id: <TextArrayInput asNumber source="" data-testid={`${testId}_NumericIdFilter`} />,
    type: (
      <SelectArrayInput
        choices={collectiveOperationTypeChoices}
        source=""
        data-testid={`${testId}_TypeFilter`}
      />
    ),
    status: (
      <SelectArrayInput
        choices={b2bCampaignStatusChoices.filter((choice) => statusFilters.includes(choice.id))}
        source=""
        data-testid={`${testId}_StatusFilter`}
      />
    ),
    from_created_at: <DateInput source="" data-testid={`${testId}_FromCreatedAtFilter`} />,
    to_created_at: <DateInput source="" data-testid={`${testId}_ToCreatedAtFilter`} />,
  });

const defaultSort: RaSort<GetCollectiveOperationsQuery> = { field: 'created_at', order: 'DESC' };

const CollectiveOperationNumericIdField = () => {
  const record = useRecordContext<CollectiveOperationListItem>();

  if (!record) {
    return null;
  }

  return (
    <IdField<CollectiveOperationListItem>
      source="numeric_id"
      referencePath="show"
      reference={record.operation_type === 'campaign' ? 'campaigns' : 'savings_funds_requests'}
      data-testid={`${testId}_NumericIdField`}
    />
  );
};

const collectiveOperationsListColumns: ColumnList<CollectiveOperationListItem> = {
  id: <TextField data-testid={`${testId}_IdColumn`} />,
  numeric_id: <CollectiveOperationNumericIdField data-testid={`${testId}_NumericIdColumn`} />,
  operation_type: (
    <TextField source="operation_type" data-testid={`${testId}_OperationTypeColumn`} />
  ),
  b2b_company_id: <NumberField source="b2b_company_id" data-testid={`${testId}_CompanyIdColumn`} />,
  date: (
    <UTCDateField
      source="date"
      label="resources.bo_external.collective_operations.fields.date"
      showTime={false}
      data-testid={`${testId}_DateColumn`}
    />
  ),
  total_gross_amount: (
    <CurrencyField
      currency="EUR"
      label="resources.bo_external.collective_operations.fields.total_gross_amount"
      data-testid={`${testId}_TotalGrossAmountColumn`}
    />
  ),
  total_net_amount: (
    <CurrencyField
      currency="EUR"
      label="resources.bo_external.collective_operations.fields.total_net_amount"
      data-testid={`${testId}_TotalNetAmountColumn`}
    />
  ),
  number_of_eligible_employees: (
    <NumberField data-testid={`${testId}_NumberOfEligibleEmployeesColumn`} />
  ),
  currency: <TextField source="currency" data-testid={`${testId}_CurrencyColumn`} />,
  created_at: (
    <UTCDateField
      source="created_at"
      label="resources.bo_external.collective_operations.fields.created_at"
      data-testid={`${testId}_CreatedAtColumn`}
    />
  ),
  updated_at: (
    <UTCDateField
      source="updated_at"
      label="resources.bo_external.collective_operations.fields.updated_at"
      data-testid={`${testId}_UpdatedAtColumn`}
    />
  ),
  type: (
    <SelectChipField
      choices={collectiveOperationTypeChoices}
      data-testid={`${testId}_TypeColumn`}
    />
  ),
  status: <CampaignStatusField source="status" data-testid={`${testId}_StatusColumn`} />,
  products: <ProductsField data-testid={`${testId}_ProductsColumn`} />,
};

const defaultColumns: (keyof CollectiveOperationListItem)[] = [
  'numeric_id',
  'date',
  'total_gross_amount',
  'total_net_amount',
  'number_of_eligible_employees',
  'products',
  'type',
  'status',
  'created_at',
];

const sortableColumns: (keyof CollectiveOperationListItem)[] = [
  'numeric_id',
  'date',
  'total_gross_amount',
  'total_net_amount',
  'number_of_eligible_employees',
  'created_at',
];

const CollectiveOperationsActionsItem = styled(Box)`
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 0.5)};
`;

export const CollectiveOperationsList: FC = () => {
  const CreateButton = (
    <B2bCampaignsCreate
      resource="campaigns"
      variant="contained"
      color="info"
      size="small"
      sx={{
        fontWeight: 'bold',
        textTransform: 'capitalize',
      }}
    />
  );

  return (
    <RecordContextProvider value={{ id: 1 }}>
      <VerticalTabsLayout>
        <VerticalTab
          icon={<DraftIcon />}
          sx={{ marginTop: '15px' }}
          label="resources.bo_external.collective_operations.tabs.draft.label"
          path="draft"
          button={CreateButton}>
          <ListView operationTab="draft" />
        </VerticalTab>
        <VerticalTab
          icon={<InProgressIcon />}
          label="resources.bo_external.collective_operations.tabs.in_progress.label"
          path="in_progress"
          button={CreateButton}>
          <ListView operationTab="in_progress" />
        </VerticalTab>
        <VerticalTab
          icon={<HistoryIcon />}
          label="resources.bo_external.collective_operations.tabs.history.label"
          path="history"
          button={CreateButton}>
          <ListView operationTab="history" />
        </VerticalTab>
        <VerticalTab
          icon={<MigrationIcon />}
          label="resources.bo_external.collective_operations.tabs.migrated.label"
          path="migrated">
          <MigratedCampaignsListView />
        </VerticalTab>
      </VerticalTabsLayout>
    </RecordContextProvider>
  );
};

const ListView = ({ operationTab }: { operationTab: B2bCampaignTab }) => {
  const displayedColumns = useSelectedColumns<CollectiveOperationListItem>({
    preferences: 'collective_operations.list.columns',
    defaultColumns,
  });

  const [activeFilters, setActiveFilters] = useState<Record<string, unknown>>({});

  const statusFilter = D.keys(B2bCampaignStatusTabMapping).filter(
    (status) => B2bCampaignStatusTabMapping[status] === operationTab,
  );

  const filtersInputFinal = useMemo(
    () => collectiveOperationsFilters(statusFilter),
    [statusFilter],
  );

  const statusFiltersFinal = useMemo(
    () => activeFilters.status ?? statusFilter,
    [activeFilters, statusFilter],
  );

  return (
    <List
      actions={
        <TopToolbar>
          <CollectiveOperationsActionsItem>
            <FilterButtonWithValues onChange={setActiveFilters} />
          </CollectiveOperationsActionsItem>
        </TopToolbar>
      }
      exporter={false}
      sort={defaultSort}
      filters={filtersInputFinal}
      filter={{ status: statusFiltersFinal }}
      disableSyncWithLocation={true}
      empty={false}
      sx={{
        '& .RaList-main': {
          width: '100%',
        },
      }}>
      <DatagridTyped<CollectiveOperationListItem>
        rowClick="show"
        bulkActionButtons={false}
        columnRegistry={collectiveOperationsListColumns}
        displayedColumns={displayedColumns}
        sortableColumns={sortableColumns}
      />
    </List>
  );
};

const FilterButtonWithValues = ({
  onChange,
}: {
  onChange: (filters: Record<string, unknown>) => void;
}) => {
  const { filterValues } = useListContext<CollectiveOperationListItem>();

  useEffect(() => {
    onChange(filterValues ?? {});
  }, [filterValues, onChange]);

  return <FilterButton data-testid={`${testId}_FilterButton`} />;
};
